import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, FileText, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminPages() {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [draft, setDraft] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/pages");
    setPages(data || []);
    if (!activeSlug && (data || []).length) {
      pick((data || [])[0]);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (p) => {
    setActiveSlug(p.slug);
    setDraft({ title: p.title || "", body: p.body || "" });
  };

  const save = async () => {
    if (!activeSlug) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/pages/${activeSlug}`, draft);
      toast.success("Page saved");
      setPages((rows) => rows.map((r) => r.slug === activeSlug ? data : r));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const active = pages.find((p) => p.slug === activeSlug);

  return (
    <div className="space-y-6" data-testid="admin-pages">
      <div>
        <h1 className="font-heading text-3xl font-semibold text-[var(--brand-accent)]">Content · Pages</h1>
        <p className="text-slate-500 mt-1">
          Edit the static pages linked from the footer (About, Careers, Press, Partners).
          Use Markdown — <code className="bg-slate-100 px-1.5 rounded">## Heading</code>,
          <code className="bg-slate-100 px-1.5 ml-1 rounded">**bold**</code>,
          <code className="bg-slate-100 px-1.5 ml-1 rounded">- bullet</code>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        {/* Page list */}
        <Card className="md:col-span-3 p-2">
          {pages.map((p) => (
            <button
              key={p.slug}
              onClick={() => pick(p)}
              data-testid={`page-pick-${p.slug}`}
              className={
                "w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-2 transition " +
                (activeSlug === p.slug
                  ? "bg-teal-50 text-[var(--brand-primary-dark)] font-semibold"
                  : "hover:bg-slate-50 text-slate-700")
              }
            >
              <FileText className="w-4 h-4 opacity-70" />
              <div className="min-w-0">
                <div className="text-sm truncate">{p.title}</div>
                <div className="text-[10px] font-mono text-slate-400">/{p.slug}</div>
              </div>
            </button>
          ))}
        </Card>

        {/* Editor */}
        <Card className="md:col-span-9 p-6 space-y-4">
          {active ? (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Editing</Label>
                  <div className="font-mono text-sm text-slate-700">/{active.slug}</div>
                </div>
                <Link to={`/${active.slug}`} target="_blank"
                      className="text-xs text-[var(--brand-primary-dark)] inline-flex items-center gap-1 hover:underline">
                  Open public page <ExternalLink className="w-3 h-3" />
                </Link>
              </div>

              <div>
                <Label>Title</Label>
                <Input
                  data-testid="page-title"
                  className="h-11"
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                />
              </div>

              <div>
                <Label>Body (Markdown)</Label>
                <Textarea
                  data-testid="page-body"
                  rows={18}
                  className="font-mono text-sm leading-relaxed"
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-between gap-3 text-xs text-slate-400">
                <div>
                  Last updated {active.updated_at ? new Date(active.updated_at).toLocaleString("en-GB") : "—"}
                  {active.updated_by ? <span> by <strong>{active.updated_by}</strong></span> : null}
                </div>
                <Button onClick={save} disabled={busy} data-testid="page-save"
                        className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full">
                  <Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save page"}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-slate-400 text-center py-10">Select a page on the left.</div>
          )}
        </Card>
      </div>
    </div>
  );
}
